import { useMemo } from "react";

import { useT } from "@repo/transifex";
import { type Category } from "~/api/products/types";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { shouldHideBarcodedProducts } from "~/utils/tills/should-hide-barcoded-products";

import { REWARDS_CATEGORY_ID } from "../types";

import { useKioskRedeemingSessionStatus } from "./use-kiosk-session";
import { useKioskProducts } from "./use-kiosk-products";

export const useKioskProductsCategories = (): Category[] => {
  const t = useT();
  const { data: shouldHideBarcoded } = useCurrentTill(
    shouldHideBarcodedProducts
  );
  const { categories, productsList, productsRedeemable } = useKioskProducts();
  const redeemingSessionStatus = useKioskRedeemingSessionStatus();

  return useMemo(() => {
    // Filter products based on barcode visibility setting
    const visibleProducts = shouldHideBarcoded
      ? productsList.filter(
          (product) => product.product.barcodeIds.length === 0
        )
      : productsList;

    // Filter categories to only include those with visible products
    const filteredCategories = categories.filter((category) =>
      visibleProducts.some(
        (product) => product.product.categoryId === category.id
      )
    );

    if (redeemingSessionStatus === "active" && productsRedeemable.length > 0) {
      return [
        {
          id: REWARDS_CATEGORY_ID,
          name: t("Available Rewards")
        },
        ...filteredCategories
      ];
    }

    return filteredCategories;
  }, [
    categories,
    productsList,
    redeemingSessionStatus,
    productsRedeemable,
    t,
    shouldHideBarcoded
  ]);
};
